/* ------------------------------------------------------------
   FIGHTERS GEAR SPOTLIGHT - parallax, reveal, lazy background video
   Extracted from the section's javascript block.

   PERF: the background video no longer carries autoplay or an inline
   source element. It is attached here, only when the section nears the
   viewport, and never for reduced-motion / Save-Data / 2G visitors -
   they keep the poster image.
   ------------------------------------------------------------ */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const small = window.matchMedia('(max-width: 749px)');
  const conn = navigator.connection || {};
  const posterOnly =
    reduced.matches || conn.saveData === true || /(^|-)2g$/.test(conn.effectiveType || '');

  const roots = new Set();
  let ticking = false;

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      roots.forEach((root) => {
        if (!root.__fcVisible) return;
        const rect = root.getBoundingClientRect();
        const vh = window.innerHeight || 1;
        const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        const clamped = Math.max(-1.2, Math.min(1.2, progress));
        const depth = root.__fcParallax || 0;
        root.style.setProperty('--fc-py', (clamped * depth).toFixed(2) + 'px');
        root.style.setProperty('--fc-cy', (clamped * depth * 0.22).toFixed(2) + 'px');
      });
    });
  }

  function syncVideo(root) {
    if (posterOnly) return;
    const video = root.querySelector('[data-fc-video]');
    if (!video) return;

    const desktop = video.dataset.desktop || '';
    const mobile = video.dataset.mobile || '';
    const wanted = small.matches && mobile ? mobile : desktop;
    if (!wanted || video.__fcSrc === wanted) return;

    video.__fcSrc = wanted;
    video.preload = 'auto';
    video.src = wanted;
    video.load();

    const reveal = () => video.classList.add('is-playing');
    video.addEventListener('playing', reveal, { once: true });
    video.addEventListener('loadeddata', reveal, { once: true });

    const playing = video.play();
    if (playing && playing.catch) playing.catch(() => {});
  }

  function init(root) {
    if (root.dataset.fcGearReady === 'true') return;
    root.dataset.fcGearReady = 'true';

    const motion = root.dataset.motion === 'true' && !reduced.matches;
    root.__fcParallax = motion ? parseFloat(root.dataset.parallax || '40') : 0;

    const video = root.querySelector('[data-fc-video]');
    if (video && !posterOnly) {
      if ('IntersectionObserver' in window) {
        // Attach the source only when the section is close to the viewport,
        // then pause/resume it as it scrolls in and out.
        new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                syncVideo(root);
                const p = video.play();
                if (p && p.catch) p.catch(() => {});
              } else if (video.__fcSrc) {
                video.pause();
              }
            });
          },
          { threshold: 0.05, rootMargin: '200px' }
        ).observe(root);
      } else {
        syncVideo(root);
      }
    }

    if (!motion || !('IntersectionObserver' in window)) {
      root.classList.add('fc-gear--in');
      return;
    }

    root.classList.add('fc-gear--anim');
    roots.add(root);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.__fcVisible = entry.isIntersecting;
          if (entry.isIntersecting) entry.target.classList.add('fc-gear--in');
        });
        onScroll();
      },
      { threshold: 0, rootMargin: '12% 0px 12% 0px' }
    );

    observer.observe(root);
    onScroll();
  }

  function onResize() {
    // Only re-point videos that have already been attached, so a resize
    // never triggers a download the visitor did not scroll to.
    document.querySelectorAll('[data-fc-gear]').forEach((root) => {
      const video = root.querySelector('[data-fc-video]');
      if (video && video.__fcSrc) syncVideo(root);
    });
    onScroll();
  }

  function boot() {
    document.querySelectorAll('[data-fc-gear]').forEach(init);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-fc-gear]').forEach((root) => {
      init(root);
      root.classList.add('fc-gear--in');
    });
  });

  document.addEventListener('shopify:section:select', (event) => {
    event.target
      .querySelectorAll('[data-fc-gear]')
      .forEach((root) => root.classList.add('fc-gear--in'));
  });
})();
